/**
 * Markdown parser for PDF export
 * Splits report content into text blocks and chart blocks
 */

export interface TextBlock {
  type: 'text';
  content: string;
}

export interface ChartBlock {
  type: 'chart';
  index: number;
  config: Record<string, unknown>;
}

export interface ScorecardBlock {
  type: 'scorecard';
  sectionTitle: string;
  summaryText: string;
  subtitle: string;
  values: Array<{ value: string; label: string }>;
}

export type ContentBlock = TextBlock | ChartBlock | ScorecardBlock;

// Regex to match graph code blocks in markdown
const GRAPH_BLOCK_REGEX = /```graph\s*\n([\s\S]*?)\n```/g;

// Match scorecard HTML blocks by counting balanced div tags
function findScorecardBlocks(content: string): Array<{ start: number; end: number; html: string }> {
  const blocks: Array<{ start: number; end: number; html: string }> = [];
  const marker = 'data-scorecard="true"';
  let searchFrom = 0;

  while (searchFrom < content.length) {
    const markerIdx = content.indexOf(marker, searchFrom);
    if (markerIdx === -1) break;

    // Find the opening <div that contains the marker
    const openIdx = content.lastIndexOf('<div', markerIdx);
    if (openIdx === -1) { searchFrom = markerIdx + 1; continue; }

    // Count balanced divs to find the matching close
    let depth = 0;
    let i = openIdx;
    while (i < content.length) {
      if (content.startsWith('<div', i)) {
        depth++;
        i += 4;
      } else if (content.startsWith('</div>', i)) {
        depth--;
        if (depth === 0) {
          blocks.push({ start: openIdx, end: i + 6, html: content.slice(openIdx, i + 6) });
          break;
        }
        i += 6;
      } else {
        i++;
      }
    }
    searchFrom = (blocks.length > 0 ? blocks[blocks.length - 1].end : markerIdx + 1);
  }
  return blocks;
}

/**
 * Parses a scorecard HTML block into structured data
 */
function parseScorecardHTML(html: string): ScorecardBlock | null {
  try {
    // Extract section title
    const titleMatch = html.match(/text-transform:uppercase[^>]*>([^<]+)</);
    const sectionTitle = titleMatch?.[1]?.trim() || '';

    // Extract summary text (strip HTML tags)
    const summaryMatch = html.match(/margin-bottom:20px[^>]*>([\s\S]*?)<\/div>/);
    const summaryText = summaryMatch?.[1]?.replace(/<[^>]+>/g, '').trim() || '';

    // Extract subtitle
    const subtitleMatch = html.match(/border-top:1px[^>]*>([^<]+)</);
    const subtitle = subtitleMatch?.[1]?.trim() || '';

    // Extract values and labels
    const values: Array<{ value: string; label: string }> = [];
    const valueMatches = html.matchAll(/font-size:28px[^>]*>([^<]+)<\/div>\s*<div[^>]*>([^<]+)<\/div>/g);
    for (const match of valueMatches) {
      values.push({ value: match[1].trim(), label: match[2].trim() });
    }

    return { type: 'scorecard', sectionTitle, summaryText, subtitle, values };
  } catch {
    return null;
  }
}

/**
 * Parses report markdown content into an array of text and chart blocks
 * @param content - Raw markdown content with embedded graph blocks
 * @returns Array of content blocks in order
 */
export function parseReportContent(content: string): ContentBlock[] {
  // Build a unified list of special blocks (scorecards + charts) sorted by position
  const specialBlocks: Array<{ start: number; end: number; block: ContentBlock }> = [];

  // Find scorecard blocks using balanced div matching
  const scorecardBlocks = findScorecardBlocks(content);
  for (const sb of scorecardBlocks) {
    const parsed = parseScorecardHTML(sb.html);
    if (parsed) {
      specialBlocks.push({
        start: sb.start,
        end: sb.end,
        block: parsed,
      });
    }
  }

  // Find chart blocks
  GRAPH_BLOCK_REGEX.lastIndex = 0;
  let chartMatch;
  let chartIndex = 0;
  while ((chartMatch = GRAPH_BLOCK_REGEX.exec(content)) !== null) {
    try {
      const config = JSON.parse(chartMatch[1]);
      specialBlocks.push({
        start: chartMatch.index,
        end: chartMatch.index + chartMatch[0].length,
        block: { type: 'chart', index: chartIndex, config },
      });
      chartIndex++;
    } catch {
      // If JSON parsing fails, skip
    }
  }

  // Sort by position in content
  specialBlocks.sort((a, b) => a.start - b.start);

  // Build final blocks list with text between special blocks
  const blocks: ContentBlock[] = [];
  let lastIndex = 0;

  for (const special of specialBlocks) {
    const textBefore = content.slice(lastIndex, special.start).trim();
    if (textBefore) {
      blocks.push({ type: 'text', content: textBefore });
    }
    blocks.push(special.block);
    lastIndex = special.end;
  }

  // Add remaining text after last special block
  const remainingText = content.slice(lastIndex).trim();
  if (remainingText) {
    blocks.push({ type: 'text', content: remainingText });
  }

  return blocks;
}

/**
 * Extracts all chart configurations from markdown content
 * @param content - Raw markdown content
 * @returns Array of chart configs with their indices
 */
export function extractChartConfigs(content: string): Array<{ index: number; config: Record<string, unknown> }> {
  const configs: Array<{ index: number; config: Record<string, unknown> }> = [];

  // Reset regex state
  GRAPH_BLOCK_REGEX.lastIndex = 0;

  let match;
  let index = 0;
  while ((match = GRAPH_BLOCK_REGEX.exec(content)) !== null) {
    try {
      const config = JSON.parse(match[1]);
      configs.push({ index, config });
      index++;
    } catch {
      // Skip invalid JSON
    }
  }

  return configs;
}

/**
 * Strips chart blocks from markdown, leaving only text content
 * @param content - Raw markdown content
 * @returns Markdown with chart blocks removed
 */
export function stripChartBlocks(content: string): string {
  return content.replace(GRAPH_BLOCK_REGEX, '').trim();
}

/**
 * Parses markdown text into structured sections for PDF rendering
 * @param text - Markdown text (without chart blocks)
 * @returns Array of parsed sections with type and content
 */
export interface ListItem {
  text: string;
  depth: number; // 0 = top level, 1 = first nesting, etc.
}

export interface TableCell {
  text: string;
  bold: boolean;
}

export interface ParsedTable {
  headers: TableCell[];
  rows: TableCell[][];
}

export interface MarkdownSection {
  type: 'h1' | 'h2' | 'h3' | 'paragraph' | 'list' | 'table' | 'blockquote' | 'hr';
  content: string;
  items?: string[]; // For flat lists (deprecated, kept for compatibility)
  listItems?: ListItem[]; // For nested lists with depth info
  parsedTable?: ParsedTable; // For structured table rendering
}

export function parseMarkdownSections(text: string): MarkdownSection[] {
  const sections: MarkdownSection[] = [];
  const lines = text.split('\n');

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();

    // Skip empty lines
    if (!line) {
      i++;
      continue;
    }

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(line)) {
      sections.push({ type: 'hr', content: '' });
      i++;
      continue;
    }

    // Headers
    if (line.startsWith('# ')) {
      sections.push({ type: 'h1', content: line.slice(2).trim() });
      i++;
      continue;
    }
    if (line.startsWith('## ')) {
      sections.push({ type: 'h2', content: line.slice(3).trim() });
      i++;
      continue;
    }
    if (line.startsWith('### ')) {
      sections.push({ type: 'h3', content: line.slice(4).trim() });
      i++;
      continue;
    }
    // h4–h6 render as h3 — deeper heading levels than the PDF's type scale.
    // Without this branch a '#### ' line matched nothing: the paragraph
    // collector refuses '#'-prefixed lines, the index never advanced, and the
    // parse loop spun forever (froze the tab on the strategy playbook).
    if (/^#{4,6}\s/.test(line)) {
      sections.push({ type: 'h3', content: line.replace(/^#{4,6}\s/, '').trim() });
      i++;
      continue;
    }

    // Lists (unordered) - with nested list support
    if (/^[-*+]\s/.test(line)) {
      const listItems: ListItem[] = [];
      while (i < lines.length) {
        const currentLine = lines[i];
        // Count leading spaces to determine depth
        const leadingSpaces = currentLine.match(/^(\s*)/)?.[1].length || 0;
        const trimmedLine = currentLine.trim();

        // Check if it's a list item (unordered or ordered)
        if (/^[-*+]\s/.test(trimmedLine)) {
          const depth = Math.floor(leadingSpaces / 2); // 2 spaces per depth level
          const text = trimmedLine.slice(2).trim();
          listItems.push({ text, depth });
          i++;
        } else if (/^\d+\.\s/.test(trimmedLine)) {
          const depth = Math.floor(leadingSpaces / 2);
          const text = trimmedLine.replace(/^\d+\.\s/, '').trim();
          listItems.push({ text, depth });
          i++;
        } else if (leadingSpaces > 0 && trimmedLine && listItems.length > 0) {
          // Continuation of previous list item (indented non-list line)
          listItems[listItems.length - 1].text += ' ' + trimmedLine;
          i++;
        } else {
          break;
        }
      }
      sections.push({ type: 'list', content: '', listItems });
      continue;
    }

    // Lists (ordered) - with nested list support
    if (/^\d+\.\s/.test(line)) {
      const listItems: ListItem[] = [];
      while (i < lines.length) {
        const currentLine = lines[i];
        const leadingSpaces = currentLine.match(/^(\s*)/)?.[1].length || 0;
        const trimmedLine = currentLine.trim();

        if (/^\d+\.\s/.test(trimmedLine)) {
          const depth = Math.floor(leadingSpaces / 2);
          const text = trimmedLine.replace(/^\d+\.\s/, '').trim();
          listItems.push({ text, depth });
          i++;
        } else if (/^[-*+]\s/.test(trimmedLine)) {
          const depth = Math.floor(leadingSpaces / 2);
          const text = trimmedLine.slice(2).trim();
          listItems.push({ text, depth });
          i++;
        } else if (leadingSpaces > 0 && trimmedLine && listItems.length > 0) {
          listItems[listItems.length - 1].text += ' ' + trimmedLine;
          i++;
        } else {
          break;
        }
      }
      sections.push({ type: 'list', content: '', listItems });
      continue;
    }

    // Blockquotes
    if (line.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        // Strip the > prefix and optional space
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ''));
        i++;
      }
      sections.push({ type: 'blockquote', content: quoteLines.join('\n') });
      continue;
    }

    // Tables (structured parsing) — require pipe at start or a separator row next
    if (line.includes('|') && lines[i + 1]?.trim().includes('|') && /^\|/.test(line.trim())) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].includes('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      const parsedTable = parseMarkdownTable(tableLines);
      sections.push({ type: 'table', content: tableLines.join('\n'), parsedTable });
      continue;
    }

    // Paragraph - collect consecutive non-special lines
    const paragraphLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith('#') &&
      !lines[i].trim().startsWith('>') &&
      !/^[-*+]\s/.test(lines[i].trim()) &&
      !/^\d+\.\s/.test(lines[i].trim()) &&
      !/^[-*_]{3,}$/.test(lines[i].trim()) &&
      !/^\|/.test(lines[i].trim())
    ) {
      paragraphLines.push(lines[i].trim());
      i++;
    }

    if (paragraphLines.length > 0) {
      sections.push({ type: 'paragraph', content: paragraphLines.join(' ') });
    } else {
      // Guarantee progress: if no branch consumed this line, skip it rather
      // than spin — an unrecognised token must never freeze the caller.
      i++;
    }
  }

  return sections;
}

/**
 * Normalizes Unicode characters that may not render correctly in PDFs
 * Converts special hyphens, dashes, quotes, etc. to ASCII equivalents
 */
export function normalizeForPDF(text: string): string {
  return text
    // Non-breaking hyphen (U+2011) -> regular hyphen
    .replace(/\u2011/g, '-')
    // Figure dash (U+2012) -> regular hyphen
    .replace(/\u2012/g, '-')
    // En dash (U+2013) -> regular hyphen
    .replace(/\u2013/g, '-')
    // Em dash (U+2014) -> regular hyphen
    .replace(/\u2014/g, '-')
    // Minus sign (U+2212) -> regular hyphen
    .replace(/\u2212/g, '-')
    // Left single quote (U+2018) -> apostrophe
    .replace(/\u2018/g, "'")
    // Right single quote (U+2019) -> apostrophe
    .replace(/\u2019/g, "'")
    // Left double quote (U+201C) -> quote
    .replace(/\u201C/g, '"')
    // Right double quote (U+201D) -> quote
    .replace(/\u201D/g, '"')
    // Horizontal ellipsis (U+2026) -> three dots
    .replace(/\u2026/g, '...')
    // Non-breaking space (U+00A0) -> regular space
    .replace(/\u00A0/g, ' ');
}

/**
 * Strips markdown formatting from text (bold, italic, code, links)
 * @param text - Text with markdown formatting
 * @returns Plain text
 */
export function stripMarkdownFormatting(text: string): string {
  return normalizeForPDF(
    text
      // HTML trend arrow spans: <span class="trend-improving">↑ 12%</span> -> ↑ 12%
      .replace(/<span\s+class="trend-[^"]*">([^<]+)<\/span>/g, '$1')
      // Links: [text](url) -> text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Bold: **text** or __text__ -> text
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      // Italic: *text* or _text_ -> text
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      // Inline code: `code` -> code
      .replace(/`([^`]+)`/g, '$1')
      // Strikethrough: ~~text~~ -> text
      .replace(/~~([^~]+)~~/g, '$1')
  );
}

/**
 * Extracts trend arrow colour info from HTML span for PDF rendering
 * Returns { text, color } where color is the appropriate hex value
 */
export function parseTrendArrow(text: string): { text: string; color: string } | null {
  const match = text.match(/<span\s+class="(trend-[^"]+)">([^<]+)<\/span>/);
  if (!match) return null;

  const colorMap: Record<string, string> = {
    'trend-improving': '#059669',
    'trend-steady': '#D97706',
    'trend-declining': '#DC2626',
  };

  return {
    text: match[2].trim(),
    color: colorMap[match[1]] || '#000000',
  };
}

/**
 * Represents a text segment that may contain a link
 */
export interface TextSegment {
  text: string;
  url?: string;
  italic?: boolean;
  bold?: boolean;
}

/**
 * Parses text containing markdown links into segments
 * Each segment is either plain text or a link with text and URL
 * @param text - Text potentially containing [text](url) markdown links
 * @returns Array of text segments
 */
export function parseTextWithLinks(text: string): TextSegment[] {
  // Detect and strip outer italic/bold markers, preserving the style intent
  let processedText = text;
  let isItalic = false;
  let isBold = false;
  // Check bold first (** wrapping)
  if (/^\*\*/.test(processedText) && /\*\*$/.test(processedText)) {
    processedText = processedText.slice(2, -2);
    isBold = true;
  }
  // Then check italic (* wrapping, not **)
  if (!isBold && /^\*[^*]/.test(processedText) && /[^*]\*$/.test(processedText)) {
    processedText = processedText.slice(1, -1);
    isItalic = true;
  }

  const segments: TextSegment[] = [];
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(processedText)) !== null) {
    // Add plain text before this link
    if (match.index > lastIndex) {
      const plainText = processedText.slice(lastIndex, match.index);
      // Strip other markdown formatting from plain text
      segments.push({ text: stripMarkdownFormatting(plainText) });
    }

    // Add the link segment (normalize the link text too)
    segments.push({
      text: normalizeForPDF(match[1]), // Link text
      url: match[2],  // URL
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining plain text after last link
  if (lastIndex < processedText.length) {
    const remainingText = processedText.slice(lastIndex);
    segments.push({ text: stripMarkdownFormatting(remainingText) });
  }

  // If no links found, return the whole text stripped of formatting
  if (segments.length === 0) {
    segments.push({ text: stripMarkdownFormatting(processedText) });
  }

  // Apply outer italic/bold style to all segments
  if (isItalic || isBold) {
    for (const seg of segments) {
      if (isItalic) seg.italic = true;
      if (isBold) seg.bold = true;
    }
  }

  return segments;
}

/**
 * Parses markdown table lines into a structured table with headers and rows
 * Handles separator rows (|---|---|) and bold cell content
 */
function parseMarkdownTable(lines: string[]): ParsedTable {
  const parseRow = (line: string): TableCell[] => {
    return line
      .split('|')
      .map(cell => cell.trim())
      .filter(cell => cell.length > 0)
      .map(cell => {
        const bold = cell.startsWith('**') && cell.endsWith('**');
        const text = stripMarkdownFormatting(cell);
        return { text, bold };
      });
  };

  const isSeparator = (line: string): boolean => {
    return /^\|?\s*[-:]+\s*(\|\s*[-:]+\s*)+\|?\s*$/.test(line.trim());
  };

  let headers: TableCell[] = [];
  const rows: TableCell[][] = [];

  for (let i = 0; i < lines.length; i++) {
    if (isSeparator(lines[i])) continue; // Skip separator rows

    const cells = parseRow(lines[i]);
    if (cells.length === 0) continue;

    // First non-separator row is the header
    if (headers.length === 0) {
      headers = cells;
    } else {
      rows.push(cells);
    }
  }

  return { headers, rows };
}
